import axios from 'axios';

// Use relative URL to leverage Vite proxy in development
const API_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) =>
    api.post('/auth/login', { email, password }),
  logout: () =>
    api.post('/auth/logout'),
  refreshToken: (refresh_token) =>
    api.post('/auth/refresh', { refresh_token }),
  resetPassword: (email) =>
    api.post('/auth/reset-password', { email }),
  verifyResetToken: (token) =>
    api.post('/auth/verify-reset-token', { token }),
  resetPasswordConfirm: (token, new_password) =>
    api.post('/auth/reset-password-confirm', { token, new_password }),
  changePassword: (access_token, new_password) =>
    api.post('/auth/change-password', { access_token, new_password }),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Folders API
export const foldersAPI = {
  getAll: () => api.get('/folders'),
  getById: (id) => api.get(`/folders/${id}`),
  create: (data) => api.post('/folders', data),
  update: (id, data) => api.put(`/folders/${id}`, data),
  delete: (id) => api.delete(`/folders/${id}`),
  getPermissions: (id) => api.get(`/folders/${id}/permissions`),
  grantPermission: (id, data) => api.post(`/folders/${id}/permissions`, data),
  revokePermission: (id, userId) => api.delete(`/folders/${id}/permissions/${userId}`),
  getRolePermissions: (id) => api.get(`/folders/${id}/role-permissions`),
  setRolePermissions: (id, data) => api.put(`/folders/${id}/role-permissions`, data),
  propagateRolePermissions: (id, data) => api.put(`/folders/${id}/role-permissions/propagate`, data),
};

// Documents API
export const documentsAPI = {
  getAll: (params) => api.get('/documents', { params }),
  getById: (id) => api.get(`/documents/${id}`),
  checkDuplicates: (data) => api.post('/documents/check-duplicates', data),
  upload: (formData, onUploadProgress) =>
    api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }),
  bulkUpload: (formData, onUploadProgress) =>
    api.post('/documents/upload/bulk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  download: (id) =>
    api.get(`/documents/${id}/download`, {
      responseType: 'blob',
      headers: {
        'Accept': '*/*',
      }
    }),
  getVersions: (id) => api.get(`/documents/${id}/versions`),
  downloadVersion: (docId, versionId) =>
    api.get(`/documents/${docId}/versions/${versionId}/download`, {
      responseType: 'blob',
      headers: {
        'Accept': '*/*',
      }
    }),
  uploadVersion: (id, formData) =>
    api.post(`/documents/${id}/versions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  restoreVersion: (docId, versionId, data) =>
    api.post(`/documents/${docId}/versions/${versionId}/restore`, data),
  getPermissions: (id) => api.get(`/documents/${id}/permissions`),
  grantPermission: (id, data) => api.post(`/documents/${id}/permissions`, data),
  revokePermission: (id, userId) => api.delete(`/documents/${id}/permissions/${userId}`),
  getRolePermissions: (id) => api.get(`/documents/${id}/role-permissions`),
  setRolePermissions: (id, data) => api.put(`/documents/${id}/role-permissions`, data),
  reprocessAI: (id) => api.post(`/documents/${id}/reprocess`),
};

// Financial API
export const financialAPI = {
  getTransactions: (params) => api.get('/financial/transactions', { params }),
  getTransaction: (id) => api.get(`/financial/transactions/${id}`),
  createTransaction: (data) => api.post('/financial/transactions', data),
  getVendors: () => api.get('/financial/vendors'),
  createVendor: (data) => api.post('/financial/vendors', data),
  getCustomers: () => api.get('/financial/customers'),
  createCustomer: (data) => api.post('/financial/customers', data),
  getDashboardStats: () => api.get('/financial/dashboard-stats'),
  getReconciliations: (params) => api.get('/financial/reconciliations', { params }),
  getReconciliation: (id) => api.get(`/financial/reconciliations/${id}`),
  createReconciliation: (data) => api.post('/financial/reconciliations', data),
  updateReconciliation: (id, data) => api.put(`/financial/reconciliations/${id}`, data),
};

// Search API
export const searchAPI = {
  search: (params) => api.get('/search', { params }),
  advancedSearch: (data) => api.post('/search/advanced', data),
};

// Chatbot API
export const chatbotAPI = {
  query: (data) => api.post('/chatbot/query', data, { timeout: 300000 }), // 5 minute timeout for long-running queries
  getConversations: (includeDeleted = false) =>
    api.get('/chatbot/conversations', { params: { include_deleted: includeDeleted } }),
  getMessages: (id) => api.get(`/chatbot/conversations/${id}/messages`),
  softDelete: (conversationIds) => api.post('/chatbot/conversations/soft-delete', { conversation_ids: conversationIds }),
  restore: (conversationIds) => api.post('/chatbot/conversations/restore', { conversation_ids: conversationIds }),
  deleteConversation: (id) => api.delete(`/chatbot/conversations/${id}`),
};

// Audit API
export const auditAPI = {
  getLogs: (params) => api.get('/admin/audit-log', { params }),
};

// Admin API
export const adminAPI = {
  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  getSystemHealth: () => api.get('/admin/system-health'),

  // Settings
  getSettings: (includeSensitive = true) => api.get('/admin/settings', { params: { include_sensitive: includeSensitive } }),
  getSetting: (key) => api.get(`/admin/settings/${key}`),
  getPublicSettings: () => api.get('/admin/public-settings'),
  getPublicSetting: (key) => api.get(`/admin/public-settings/${key}`),
  updateSetting: (id, data) => api.put(`/admin/settings/${id}`, data),

  // SMTP/Email
  testSmtp: (config) => api.post('/admin/test-smtp', config),
  testEmail: (email) => api.post('/admin/test-email', { email }),
  getEmailQueueStatus: () => api.get('/admin/email-queue-status'),
  processEmailQueue: () => api.post('/admin/email-queue-process'),
  getSmtpStatus: () => api.get('/admin/smtp-status'),

  // Security Events
  getSecurityEvents: (params) => api.get('/admin/security/events', { params }),
  resolveSecurityEvent: (id) => api.put(`/admin/security/events/${id}/resolve`),
  getFailedLogins: (params) => api.get('/admin/security/failed-logins', { params }),
  getLockouts: (params) => api.get('/admin/security/lockouts', { params }),
  unlockAccount: (id) => api.post(`/admin/security/lockouts/${id}/unlock`),

  // User Activity
  getUserActivity: (params) => api.get('/admin/users/activity', { params }),

  // Company Logo
  uploadLogo: (formData) => api.post('/admin/upload-logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getCompanyLogo: () => api.get('/admin/company-logo'),
};

// CSV Extraction API
export const csvAPI = {
  extract: (formData) =>
    api.post('/csv/extract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  extractCustom: (data) => api.post('/csv/extract-custom', data),
  getExtractions: (documentId) =>
    api.get('/csv/extractions', { params: documentId ? { document_id: documentId } : {} }),
  download: (extractionId) => api.get(`/csv/download/${extractionId}`),
  saveTemplate: (formData) =>
    api.post('/csv/templates', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getTemplates: () => api.get('/csv/templates'),
  deleteTemplate: (templateId) => api.delete(`/csv/templates/${templateId}`),
};

// Actions API
export const actionsAPI = {
  getMyActions: (params) => api.get('/actions/my-actions', { params }),
  getMyStats: () => api.get('/actions/my-stats'),
  respond: (id, data) => api.post(`/actions/${id}/respond`, data),
  bulkRespond: (data) => api.post('/actions/bulk-respond', data),
  create: (data) => api.post('/actions', data),
  bulkCreate: (data) => api.post('/actions/bulk-create', data),
};

// SharePoint API
export const sharepointAPI = {
  // OAuth & Connection Management
  getAuthUrl: () => api.get('/sharepoint/auth/url'),
  getConnections: () => api.get('/sharepoint/connections'),
  disconnect: (connectionId) => api.delete(`/sharepoint/connections/${connectionId}`),

  // Site & Drive Browsing
  getSites: (connectionId, searchQuery = '') =>
    api.get(`/sharepoint/connections/${connectionId}/sites`, {
      params: searchQuery ? { search: searchQuery } : {}
    }),
  getDrives: (connectionId, siteId) =>
    api.get(`/sharepoint/connections/${connectionId}/sites/${siteId}/drives`),
  getFolderContents: (connectionId, driveId, folderId = null) =>
    api.get(`/sharepoint/connections/${connectionId}/drives/${driveId}/folders${folderId ? `/${folderId}` : ''}`),

  // Sync Operations
  saveSelection: (connectionId, selectedSites) =>
    api.post(`/sharepoint/connections/${connectionId}/save-selection`, { selectedSites }),
  startSync: (connectionId, selectedSites) =>
    api.post(`/sharepoint/connections/${connectionId}/sync`, { selectedSites }),
  getSyncStatus: (connectionId) =>
    api.get(`/sharepoint/connections/${connectionId}/sync/status`),
  getDocuments: (connectionId, params = {}) =>
    api.get(`/sharepoint/connections/${connectionId}/documents`, { params }),

  // Search
  search: (params) => api.get('/sharepoint/search', { params }),

  // Process (Download & Create Local Copy)
  processFile: (data) => api.post('/sharepoint/process', data),
};

export default api;
